<script lang="ts" setup>
import MainLogo from "~/components/MainLogo.vue";
import {capitalize, type Component, type ComputedRef, ref} from "vue";
import IconCaretDown from "~/components/sx/icons/IconCaretDown.vue";
import SxPopup from "~/components/sx/SxPopup.vue";
import PopupContentProxy from "~/components/sx/popupContent/PopupContentProxy.vue";
import {useLocaleStore} from "~/stores/useLocaleStore";
import {useUrlHashStore} from "~/stores/useUrlHashStore";
import {UiLanguages} from '@common/ui-kit';
import {LANG} from "~/enums/Lang";
import { onClickOutside } from '@vueuse/core'
import IconSxWechat from "~/components/sx/icons/IconSxWechat.vue";

const config = useRuntimeConfig();
const { setLocale, mappedLanguages, currentLanguage, currentIsoCode, visibleSxLangsModal } = useLocaleStore();
const { queryRefParams } = useHttpReferrer();
const { locale: localeI18n } = useI18n();
const { t } = useLocale();
const { setHash, removeHash } = useUrlHashStore();

interface NavItemType {
  id: number;
  name?: string | ComputedRef<string | undefined>;
  component?: Component | null;
  link?: string | null;
  needToTranslate: boolean;
  modal?: boolean;
}

const isDropdownVisible = ref(false);
let hideTimeout: ReturnType<typeof setTimeout> | null = null;
const triggerRef = ref<HTMLElement | null>(null)
const popupRef = ref<HTMLElement | null>(null)

const isVisibleMobileMenu = ref(false);
const navItems = ref<NavItemType[]>([
  {
    id: 1,
    name: "Прокси",
    component: markRaw(PopupContentProxy),
    link: "proxy",
    needToTranslate: true,
  },
  {
    id: 2,
    name: "Цена",
    component: null,
    link: "#prices",
    needToTranslate: true,
  },
  {
    id: 3,
    name: "Geo",
    component: null,
    link: "geo",
    needToTranslate: false,
  },
  {
    id: 5,
    name: "Партнеры",
    component: null,
    link: "partners",
    needToTranslate: true,
  },
  {
    id: 6,
    name: "Разработчикам",
    component: null,
    link: "developers",
    needToTranslate: true,
  },
  {
    id: 7,
    name: "FAQ",
    component: null,
    link: "faq",
    needToTranslate: true,
  },
  {
    id: 8,
    name: "Блог",
    component: null,
    link: "blog",
    needToTranslate: true,
  },
]);

const toggleMobileMenu = () => {
  isVisibleMobileMenu.value = !isVisibleMobileMenu.value;
  if (isVisibleMobileMenu.value) {
    document.body.style.overflowY = 'hidden';
  } else {
    document.body.style.overflowY = 'inherit'
  }
};

const clickHandle = (link: string) => {
  closeDropdown();
  if (link === "#prices") {
    setHash("prices");
  } else {
    removeHash();
  }
};

function onMouseEnter() {
  if (hideTimeout) {
    clearTimeout(hideTimeout)
    hideTimeout = null
  }
  isDropdownVisible.value = true
}

function onMouseLeave() {
  hideTimeout = setTimeout(() => {
    isDropdownVisible.value = false
  }, 3000)
}

function closeDropdown() {
  isDropdownVisible.value = false;
  clearTimeout(hideTimeout!);
}

onClickOutside([triggerRef, popupRef], () => {
  closeDropdown();
})

watch(visibleSxLangsModal, (value) => {
  if (value) {
    document.body.style.overflowY = 'hidden';
  } else {
    document.body.style.overflowY = 'inherit'
  }
});

onUnmounted(() => {
  if (hideTimeout) clearTimeout(hideTimeout)
})
</script>

<template>
  <header class="sx-header">
    <div class="sx-header__content">
      <div class="sx-header__left-section">
        <div class="sx-header__item--logo">
          <main-logo sx link />
        </div>
        <nav class="sx-header__item sx-header__item--nav">
          <ul class="sx-header__nav-list">
            <li
              class="sx-header__nav-item"
              ref="triggerRef"
              @mouseenter="onMouseEnter"
              @mouseleave="onMouseLeave"
            >
              <NuxtLink
                :to="`/${localeI18n}/proxy`"
                class="sx-header__nav-item-info"
                @click="clickHandle('proxy')"
              >
                {{ capitalize(t('Прокси')) }}
                <icon-caret-down
                  class="sx-header__icon-caret"
                  :class="{ active: isDropdownVisible }"
                />
              </NuxtLink>

              <transition name="fade-slide">
                <sx-popup
                  v-show="isDropdownVisible"
                  ref="popupRef"
                  class="sx-header__popup"
                >
                  <component :is="PopupContentProxy" />
                </sx-popup>
              </transition>
            </li>
            <li class="sx-header__nav-item">
              <NuxtLink
                :to="`/${localeI18n}/geo`"
                class="sx-header__nav-item-info"
                @click="clickHandle('geo')"
              >
                Geo
              </NuxtLink>
            </li>
            <li class="sx-header__nav-item">
              <NuxtLink
                :to="`/${localeI18n}/partners`"
                class="sx-header__nav-item-info"
                @click="clickHandle('partners')"
              >
                {{ capitalize(t('Партнеры')) }}
              </NuxtLink>
            </li>
            <li class="sx-header__nav-item">
              <NuxtLink
                :to="`/${localeI18n}/developers`"
                class="sx-header__nav-item-info"
                @click="clickHandle('developers')"
              >
                {{ capitalize(t('Разработчикам')) }}
              </NuxtLink>
            </li>
            <li class="sx-header__nav-item">
              <NuxtLink
                :to="`/${localeI18n}/faq`"
                class="sx-header__nav-item-info"
                @click="clickHandle('faq')"
              >
                FAQ
              </NuxtLink>
            </li>
            <li class="sx-header__nav-item">
              <NuxtLink
                :to="`/${localeI18n}/blog`"
                class="sx-header__nav-item-info"
                @click="clickHandle('blog')"
              >
                {{ capitalize(t('Блог')) }}
              </NuxtLink>
            </li>
          </ul>
        </nav>
      </div>

      <div class="sx-header__item sx-header__auth-btn-wrapper">
        <button class="sx-header__btn-chat" id="hde-button">
          <IconSxWechat />
          {{ t('Онлайн чат') }}
        </button>

        <UiLanguages
          v-model:is-show="visibleSxLangsModal"
          for-header
          v-model="currentLanguage"
          :locales="mappedLanguages"
          @change="setLocale($event)"
          class="sx-header__languages"
        />
        <a
          class="sx-header__auth-btn"
          :href="config.public.signinUrl + queryRefParams"
        >
          {{ currentIsoCode === LANG.RU ? 'Войти' : 'Login' }}
        </a>
        <a
          class="sx-header__auth-btn sx-header__auth-btn--rounded"
          :href="config.public.signupUrl + queryRefParams"
        >
          {{ currentIsoCode === LANG.EN ? 'Sign up' :t('Регистрация') }}
        </a>
      </div>
    </div>
    <div class="sx-header__content--mobile">
      <div class="icon-menu" @click="toggleMobileMenu">
        <span />
        <span />
        <span />
      </div>
      <transition class="sx-header__menu">
        <nav
          v-if="isVisibleMobileMenu"
          class="sx-header__item sx-header__item--nav"
        >
          <ul class="sx-header__nav-list mobile">
            <li>
              <a
                class="auth-block"
                :href="config.public.signinUrl + queryRefParams"
                target="_blank"
              >
                <div>
                  {{ t("Создайте учетную запись или войдите в систему") }}
                </div>
              </a>
            </li>

            <li
              :class="{
                'sx-header__nav-item': true,
                'sx-header__nav-item--modal': true,
                'sx-header__nav-item--modal-open': visibleSxLangsModal,
              }"
            >
              <NuxtLink
                :to="`/${localeI18n}`"
                @click="toggleMobileMenu"
              >
                <span class="sx-header__nav-item-info-text">
                  {{ t('Главная', { context: 'На главную страницу' }) }}
                </span>
              </NuxtLink>
            </li>
            <li
              v-for="item in navItems"
              :key="item.id"
              :class="{
                'sx-header__nav-item': item.component || !item.modal,
                'sx-header__nav-item--modal': item.modal,
                'sx-header__nav-item--modal-open': visibleSxLangsModal,
              }"
            >
              <NuxtLink
                v-if="item.link"
                :to="`/${localeI18n}/${item.link}`"
                class="sx-header__nav-item-info"
                @click="toggleMobileMenu"
              >
                <span class="sx-header__nav-item-info-text">
                  {{item.needToTranslate ? capitalize(t(item.name!)) : item.name }}
                </span>
              </NuxtLink>
            </li>
          </ul>
        </nav>
      </transition>
    </div>
  </header>
</template>

<style lang="scss" scoped>
.auth-block {
  background-color: #f7f7f7;
  padding: 10px 20px;
  border-radius: 20px;
  margin: 0 10px;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #000;
}
.auth-button {
  border: 1px solid #ccc;
  padding: 10px 30px !important;
  border-radius: 25px;
}

.sx-header__nav-item-info--langs {
  max-width: 72px;
  outline: 1px solid #f0f0f0;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  transition: 0.3s;
  display: flex;
  cursor: pointer;
  margin-right: 4px;
  &:hover {
    background: #f6f6f6;
  }
}

.icon-menu {
  flex-shrink: 0;
  position: relative;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  background: transparent;
  border-radius: 10px;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  cursor: pointer;
  overflow: hidden;
  display: none;
}

.icon-menu span {
  position: absolute;
  height: 3px;
  background-color: #202027;
  display: block;
  border-radius: 6px;
  transition: 0.5s;
}

.icon-menu span:nth-child(1) {
  transform: translateY(-8px);
  width: 28px;
  right: 0;
  transition-delay: 0s;
}

.icon-menu span:nth-child(2) {
  width: 28px;
  right: 0;
}

.icon-menu span:nth-child(3) {
  transform: translateY(8px);
  width: 28px;
  right: 0;
  transition-delay: 0s;
}

.sx-header {
  color: black;
  position: relative;
  z-index: 999;
  padding: 24px 0 88px;
  font-family: AlsHauss, sans-serif;
  &__btn-chat {
    display: flex;
    gap: 4px;
    background: white;
    align-items: center;
    border-radius: 40px;
    border: 1px solid #000;
    color: #000;
    padding: 4px 8px;
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    letter-spacing: 0;
    margin-right: 16px;
    cursor: pointer;
    &:hover {
      background: #eeeeee;
    }
  }
  &__flag {
    width: 20px;
    min-width: 20px;
    height: 20px;
    margin-right: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    outline: 0.8px solid #e1e8f1;
    border-radius: 50%;
    &-inner {
      min-height: 32px !important;
      min-width: 32px !important;
      display: flex;
    }
  }

  @media (width < 1500px) {
    padding-inline: 16px;
  }

  &__left-section {
    display: flex;
    align-items: center;
    gap: 60px;
  }
  &__content {
    max-width: 1336px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    &--mobile {
      .sx-header__item {
        max-width: 100% !important;
      }
      .sx-header__menu {
        position: absolute;
        background: #fff;
        left: 0;
        top: 90px;
        width: 100%;
      }
      .sx-header__nav-list {
        padding: 1rem;
        height: calc(100vh - 90px);
        align-items: center;
        justify-content: flex-start;
        &.mobile {
          overflow: auto;
        }
      }
    }
  }

  &__languages {
    :deep(button) {
      &:hover {
        background: #eeeeee;
      }
    }
  }
  &__item {
    align-self: stretch;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    &--nav {
      width: 100%;
    }
  }
  &__icon-caret {
    &.active {
      rotate: 180deg;
      :deep([stroke]) {
        stroke: black;
      }
    }
  }
  &__nav-list {
    align-self: stretch;
    display: flex;
    width: 100%;
    align-items: center;
    gap: 42px;
    max-width: 683px;
    @media (max-width: 1499px) {
      gap: 32px;
    }
    .sx-header__nav-item--modal {
      display: flex;
      align-items: center;
      position: relative;
    }
    .sx-header__nav-item {
      display: flex;
      align-items: center;
      &:hover {
        .sx-header__nav-item-info {
          color: #535353;
        }
      }
      .sx-header__popup {
        position: absolute;
        width: max-content;
        top: 114%;
        left: 50%;
        transform: translateX(-50%);
        z-index: 100000;
        background: #fff;
        transition: translate 0.3s ease, opacity 0.3s ease;
        @media (max-width: 1399px) {
          width: 100%;
        }
      }

      &-info {
        display: flex;
        align-items: center;
        gap: 2px;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        cursor: pointer;
        position: relative;
        z-index: 100001;
        color: var(--font-sx-color-black);

        @media (width < 1500px) {
          padding-inline: 10px;
        }
        &-text {
          text-wrap: nowrap;
          color: #000;
        }
      }
    }
  }

  &__auth-btn-wrapper {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  &__auth-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    padding: 0 20px;
    height: 44px;
    border-radius: 22px;
    white-space: nowrap;
    color: #000;
    &:hover {
      background: #000000;
      color: #fff;
    }
    &--rounded {
      min-width: 138px;
      outline: 1px solid #000;
      &:hover {
        color: #000;
        background: #eeeeee;
      }
      &:active {
        background: #f7f7f7;
      }
    }
  }
}

@media (max-width: 1320px) {
  .sx-header__item--logo {
    display: none;
  }
}
@media (max-width: 1128px) {
  .sx-header {
    padding-bottom: 32px;
  }
  .sx-header__content {
    margin: 0;
  }
  .sx-header {
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    justify-content: space-between;
  }
  .sx-header__left-section, .sx-header__auth-btn {
    display: none;
  }
  .icon-menu {
    display: flex;
  }
  .sx-header__content--mobile {
    visibility: visible;
  }
  .sx-header__nav-list {
    flex-direction: column;
  }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
}
.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
}
</style>
